// tslint:disable:no-commented-code
// tslint:disable:only-arrow-functions

import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  EventHandlerPayloadResolveMoment,
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { IRecommendationTrackSource } from '@epicuro-next/models/recommendation-panels';
import * as configStripActions from '@epicuro-next/state/config-strip-state';
import { trackList } from '@epicuro-next/state/product-list-state';
import { openOverlayOutlet } from '@epicuro-next/state/router-store';
import * as trackingActions from '@epicuro-next/state/tracking';
import * as userUiActions from '@epicuro-next/state/user-ui-state';
import { PaneLocation } from '@epicuro-next/state/user-ui-state';
import { StrictOmit } from '@epicuro-next/utilities';

import {
  eventHandlerFromAction,
  IDynamicEventHandlerWithId,
} from '../utils/eventhandler-from-action';
export const itemEventPayloadPropertyName = 'itemEventPayload';

export type ProductDataTypeForRecommendationEventHandler = {
  product: unknown;
  correlationId: string | null;
  servingConfig: string | null;
  attributionToken: string | null;
  providerId: string | null;
  hash: string | null;
};

export type ProductDataTypeForOpenConfigStripEventHandler = {
  configStrip: { overlayPath: string[] };
};

export function recommendationItemClickedEventHandler(
  panelNameProvider: IDynamicBindableValue | IDynamicBindableValueCombination,
  panelTrackingIdProvider:
    | IDynamicBindableValue
    | IDynamicBindableValueCombination,
  productDataProvider: IDynamicBindableValue | IDynamicBindableValueCombination,
): IDynamicEventHandlerWithId {
  return eventHandlerFromAction(trackingActions.recommendationItemClicked, {
    id: 'recommendation-item-clicked-event-handler',
    payload$: {
      values: [panelNameProvider, productDataProvider, panelTrackingIdProvider],
      transform: function ([panelName, productDataArg, panelStyle]: [
        string,
        // tslint:disable-next-line:no-any
        ProductDataTypeForRecommendationEventHandler,
        string,
      ]) {
        const product = productDataArg.product;
        return {
          panelName,
          panelStyle,
          correlationId: productDataArg.correlationId ?? null,
          servingConfig: productDataArg.servingConfig ?? null,
          attributionToken: productDataArg.attributionToken ?? null,
          providerId: productDataArg.providerId ?? null,
          hash: productDataArg.hash ?? null,
          product,
        };
      }.toString(),
    },
  });
}

export function openConfigStripEventHandler(
  provider = 'parent',
  property = 'productData',
): IDynamicEventHandlerWithId {
  return eventHandlerFromAction(openOverlayOutlet, {
    id: 'open-config-strip-event-handler',
    payload$: {
      provider,
      property,
      transform: function (
        productDataArg: ProductDataTypeForOpenConfigStripEventHandler,
      ) {
        return productDataArg.configStrip;
      }.toString(),
    },
  });
}

export function recommendationItemShownEventHandler(
  panelNameProvider: IDynamicBindableValue | IDynamicBindableValueCombination,
  panelTrackingIdProvider:
    | IDynamicBindableValue
    | IDynamicBindableValueCombination,
  productDataProvider: IDynamicBindableValue | IDynamicBindableValueCombination,
): IDynamicEventHandlerWithId {
  return eventHandlerFromAction(trackingActions.recommendationItemsShown, {
    id: 'recommendation-item-shown-event-handler',
    payload$: {
      values: [panelNameProvider, productDataProvider, panelTrackingIdProvider],
      transform: function ([panelName, productDataArg, panelStyle]: [
        string,
        // tslint:disable-next-line:no-any
        ProductDataTypeForRecommendationEventHandler,
        string,
      ]) {
        const products = [productDataArg.product];
        return {
          panelName,
          panelStyle,
          correlationId: productDataArg.correlationId ?? null,
          servingConfig: productDataArg.servingConfig ?? null,
          attributionToken: productDataArg.attributionToken ?? null,
          hash: productDataArg.hash ?? null,
          providerId: productDataArg.providerId ?? null,
          products,
        };
      }.toString(),
    },
  });
}

type CsSetProductSourceOptions = {
  seeMore: boolean;
  payloadFetchMoment: EventHandlerPayloadResolveMoment;
};
export function configStripSetProductSource(
  providers: {
    listIdProvider: IDynamicBindableValue | IDynamicBindableValueCombination;
    panelNameProvider: IDynamicBindableValue | IDynamicBindableValueCombination;
    panelTrackingIdProvider:
      | IDynamicBindableValue
      | IDynamicBindableValueCombination;
  },
  options: CsSetProductSourceOptions = {
    seeMore: false,
    payloadFetchMoment: EventHandlerPayloadResolveMoment.AtEvent,
  },
): IDynamicEventHandlerWithId {
  const action = configStripActions.setProductSource;
  type ActionPayloadType = StrictOmit<ReturnType<typeof action>, 'type'>;
  return eventHandlerFromAction(configStripActions.setProductSource, {
    id: 'config-strip-set-product-source-event-handler',
    payloadFetchMoment: options.payloadFetchMoment,
    payload$: {
      values: [
        providers.listIdProvider,
        providers.panelNameProvider,
        providers.panelTrackingIdProvider,
        options,
      ],
      transform: function ([listId, panelName, panelStyle, optionsData]: [
        string,
        string,
        string,
        CsSetProductSourceOptions,
      ]): ActionPayloadType {
        const payload: ActionPayloadType = {
          // @ts-ignore
          source: 'LISTSOURCETYPE_PLACEHOLDER',
          details: {
            recommendationPanel: {
              panelName,
              panelStyle,
              servingConfig: 'ProductLists',
              correlationId: listId,
              seeMore: optionsData.seeMore,
            } as IRecommendationTrackSource,
            // track list id only on see more button
            ...(optionsData.seeMore
              ? {
                  listId: [listId],
                }
              : {}),
          },
        };
        return payload;
      }
        .toString()
        .replace(
          'LISTSOURCETYPE_PLACEHOLDER',
          BasketItemSource.RECOMMENDATION_PANEL,
        ),
    },
  });
}

export function trackListAction(
  providers: {
    listIdProvider: IDynamicBindableValue | IDynamicBindableValueCombination;
  },
  payloadFetchMoment = EventHandlerPayloadResolveMoment.BeforeAddEventListener,
): IDynamicEventHandlerWithId {
  const action = trackList;
  type ActionPayloadType = StrictOmit<ReturnType<typeof action>, 'type'>;
  return eventHandlerFromAction(trackList, {
    id: 'track-product-list-event-handler',
    payloadFetchMoment,
    payload$: {
      values: [providers.listIdProvider],
      transform: function ([listId]: [string]): ActionPayloadType {
        const payload: ActionPayloadType = {
          payload: {
            source: 'recommendation-panels',
            productListId: listId,
          },
        };
        return payload;
      }.toString(),
    },
  });
}

export function clearPaneContent(
  location: PaneLocation,
): IDynamicEventHandlerWithId {
  const action = userUiActions.clearPane;
  type ActionPayloadType = StrictOmit<ReturnType<typeof action>, 'type'>;
  const payload: ActionPayloadType = {
    location,
  };

  return eventHandlerFromAction(action, {
    id: 'clear-pane-content-event-handler',
    payload$: {
      values: [payload],
      transform: function ([payloadArg]: [
        ActionPayloadType,
      ]): ActionPayloadType {
        return payloadArg;
      }.toString(),
    },
  });
}
